import { useState, useMemo, useEffect, useContext, useRef } from "react";
import { GlobalContext } from "../../../Context/GlobalContext";
import usePosOrders from "../../../services/Pos/usePosOrders";
import PaymentModal from "./PaymentModel";
import { toast } from "react-toastify";
import MobileNumberModal from "./MobileNumberModal";
import KotGeneratedModal from "./KotGeneratedModal";
import useDining from "../../../services/Order/useDining";

export default function DineScreen({
  table,
  orderType,
  orderId,
  mobile,
  existingItems = [],
  reciptDetails,
  catalogueCache,
  menuCacheVersion,
  refreshCatalogueCache,
  loadingMenu,
  onBack,
  onClose,
  onHoldSuccess,
}) {
  const normalizeOrderTypeForCatalog = (type) => {
    const normalized = String(type || "")
      .toLowerCase()
      .trim();

    if (normalized === "homedelivery") return "home_delivery";
    if (normalized === "home_delivery") return "home_delivery";

    if (normalized === "dinein") return "dining";
    if (normalized === "dine-in") return "dining";
    if (normalized === "dining") return "dining";

    if (normalized === "takeway") return "takeaway";
    if (normalized === "take away") return "takeaway";
    if (normalized === "takeaway") return "takeaway";

    // Room order will use dining catalogue/menu
    if (
      normalized === "room_order" ||
      normalized === "room order" ||
      normalized === "roomorder"
    ) {
      return "dining";
    }

    return "takeaway";
  };

  const normalizedOrderType = normalizeOrderTypeForCatalog(orderType);

  const getPayloadOrderType = () => {
    const rawType = String(orderType || "")
      .toLowerCase()
      .trim();

    if (
      rawType === "room_order" ||
      rawType === "room order" ||
      rawType === "roomorder"
    ) {
      return "room_order";
    }

    return normalizedOrderType;
  };

  const payloadOrderType = getPayloadOrderType();

  const isRoomOrder = payloadOrderType === "room_order";

  const getCurrentTableNo = () => {
    const type = normalizeOrderTypeForCatalog(orderType);

    if (type !== "dining" && !isRoomOrder) return "";

    if (typeof table === "object") {
      return (
        table?.tableNo ||
        table?.table_no ||
        table?.roomNumber ||
        table?.roomNo ||
        table?.room_no ||
        table?.table ||
        table?.name ||
        ""
      );
    }

    return table || "";
  };

  const normalizeMobile = (m) => (typeof m === "object" ? m.mobile : m);

  const { userID } = useContext(GlobalContext);

  const [activeCategory, setActiveCategory] = useState("");
  const [cart, setCart] = useState([]);
  const [searchItem, setSearchItem] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [showPayment, setShowPayment] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerMobile, setCustomerMobile] = useState(normalizeMobile(mobile));
  const [customerName, setCustomerName] = useState("");
  const [showCustomize, setShowCustomize] = useState(false);
  const [selectedCartIndex, setSelectedCartIndex] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});

  // Combo flow state. Combo selections stay separate from the existing
  // regular-item customization flow so old POS behaviour is unchanged.
  const [showComboModal, setShowComboModal] = useState(false);
  const [selectedCombo, setSelectedCombo] = useState(null);
  const [comboSelections, setComboSelections] = useState({});
  const [editingComboCartIndex, setEditingComboCartIndex] = useState(null);

  const [showKotModal, setShowKotModal] = useState(false);
  const [kotOrderData, setKotOrderData] = useState(null);
  const [kotOrderId, setKotOrderId] = useState(orderId || "");
  const [kotLoading, setKotLoading] = useState(false);
  const [kotCartSnapshot, setKotCartSnapshot] = useState([]);
  const [activeOrderId, setActiveOrderId] = useState(orderId || "");

  const searchRef = useRef(null);

  const {
    catalogueStore = [],
    getCatalog,
    catalogLoading,
    generateOrderID,
    placePosOrder,
    setCatalogueStore,
    getDiscountList,
    getOrderDetails,
    getOrders,
    printKot,
  } = usePosOrders();

  const { refreshDiningCache } = useDining();

  const handleCancel = async () => {
    const date = new Date();

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    const currentDate = `${year}-${month}-${day}`;

    try {
      await getOrders({
        fromDate: currentDate,
        toDate: currentDate,
      });
    } catch (error) {
      console.error("Get POS orders on cancel error:", error);
    } finally {
      setCart([]);
      setActiveOrderId("");
      setSelectedCartIndex(null);
      setSelectedOptions({});
      setShowCustomize(false);
      closeComboConfigurator();

      onClose?.();
    }
  };

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (Array.isArray(catalogueStore) && catalogueStore.length > 0) {
      setActiveCategory(catalogueStore[0].category_name);
    } else {
      setActiveCategory("");
    }
  }, [catalogueStore]);

  useEffect(() => {
    if (!userID) return;

    const activeType = normalizeOrderTypeForCatalog(orderType);

    if (catalogueCache && Array.isArray(catalogueCache[activeType])) {
      setCatalogueStore(catalogueCache[activeType]);
      return;
    }

    getCatalog({ orderType: normalizedOrderType, force: false });
  }, [
    userID,
    orderType,
    normalizedOrderType,
    menuCacheVersion,
    catalogueCache,
  ]);

  const safeCatalogue = Array.isArray(catalogueStore) ? catalogueStore : [];

  const activeCategoryData = safeCatalogue.find(
    (cat) => cat.category_name === activeCategory,
  );

  const allItems = safeCatalogue.flatMap((cat) => cat.menu_items || []);

  const findItemByBarcode = (barcode) => {
    const code = String(barcode || "").trim();

    return allItems.find((item) => {
      if (String(item.barcodeNumber || "").trim() === code) return true;
      if (String(item.barcode_number || "").trim() === code) return true;
      if (String(item.barcode || "").trim() === code) return true;
      if (String(item.item_id || "").trim() === code) return true;

      return item.prices?.some(
        (p) =>
          String(p.barcodeNumber || "").trim() === code ||
          String(p.barcode_number || "").trim() === code ||
          String(p.barcode || "").trim() === code,
      );
    });
  };

  const getPriceByBarcode = (item, barcode) => {
    const code = String(barcode || "").trim();

    const matchedPrice = item.prices?.find(
      (p) =>
        String(p.barcodeNumber || "").trim() === code ||
        String(p.barcode_number || "").trim() === code ||
        String(p.barcode || "").trim() === code,
    );

    return matchedPrice || item.prices?.[0];
  };

  const handleBarcodeSubmit = () => {
    const code = barcodeInput.trim();

    if (!code) return;

    const item = findItemByBarcode(code);

    if (!item) {
      toast.error("Item not found");
      setBarcodeInput("");
      return;
    }

    const priceObj = getPriceByBarcode(item, code);

    addToCart(item, priceObj);
    setBarcodeInput("");
  };

  const categories = Array.isArray(catalogueStore)
    ? catalogueStore.map((cat) => cat.category_name).filter(Boolean)
    : [];

  const normalizedSearchItem = String(searchItem || "")
    .trim()
    .toLowerCase();

  // Same search box now supports category names too.
  // Matching categories are moved to the top of the sidebar.
  const matchingCategories = useMemo(() => {
    if (!normalizedSearchItem) return [];

    return categories.filter((categoryName) =>
      String(categoryName || "")
        .toLowerCase()
        .includes(normalizedSearchItem),
    );
  }, [categories, normalizedSearchItem]);

  const displayedCategories = useMemo(() => {
    if (!normalizedSearchItem || matchingCategories.length === 0) {
      return categories;
    }

    const matchedSet = new Set(matchingCategories);

    return [
      ...matchingCategories,
      ...categories.filter((categoryName) => !matchedSet.has(categoryName)),
    ];
  }, [categories, matchingCategories, normalizedSearchItem]);

  // When search matches a category, automatically open that category.
  useEffect(() => {
    if (!normalizedSearchItem || matchingCategories.length === 0) return;

    const firstMatchedCategory = matchingCategories[0];

    if (firstMatchedCategory && activeCategory !== firstMatchedCategory) {
      setActiveCategory(firstMatchedCategory);
    }
  }, [normalizedSearchItem, matchingCategories, activeCategory]);

  const isComboLine = (item) =>
    String(item?.lineType || "").toUpperCase() === "COMBO";

  const getComboSelectionSignature = (item) => {
    if (!isComboLine(item)) return "";

    try {
      return JSON.stringify(item?.selectedGroups || []);
    } catch {
      return String(item?.comboId || item?.item_id || "");
    }
  };

  const getItemKey = (item) => {
    if (isComboLine(item)) {
      return `COMBO-${item?.comboId || item?.item_id}-${getComboSelectionSignature(
        item,
      )}`;
    }

    return `${item.item_id}-${
      item.selectedPrice?.size_id || item.size_id || item.item_id
    }`;
  };

  const createComboSelectedItem = (item, priceObj) => {
    const productId = String(item?.item_id || item?.itemId || "");
    const productName = item?.item_name || item?.itemName || "";
    const itemImage = item?.photo || item?.itemImage || item?.image || "";
    const sizeWords = priceObj?.size || priceObj?.sizeWords || "";

    const rawSizeId =
      priceObj?.size_id === undefined ||
      priceObj?.size_id === null ||
      String(priceObj?.size_id || "").trim() === ""
        ? ""
        : String(priceObj.size_id);

    const sizeId = rawSizeId || productId;
    const itemPrice = String(priceObj?.price ?? priceObj?.itemPrice ?? 0);

    return {
      // placePosOrder compatible fields
      productId,
      sizeId,
      productName,
      actualName: productName,
      customization: "{}",
      hasVarient: sizeWords || "false",
      itemImage,
      itemPrice,
      quantity: "1",
      sizeWords,

      // UI compatibility fields
      itemId: productId,
      itemName: productName,
      variantId: rawSizeId || null,
      variantName: sizeWords,
    };
  };

  const createDefaultComboSelections = (combo) => {
    const defaults = {};
    const groups = Array.isArray(combo?.optionGroups) ? combo.optionGroups : [];

    groups.forEach((group) => {
      const groupKey = String(group?.optionGroupId || "");
      const minimum = Math.max(Number(group?.minimumSelection || 0), 0);
      const availableItems = Array.isArray(group?.items) ? group.items : [];

      defaults[groupKey] = availableItems.slice(0, minimum).map((item) => {
        const firstPrice = Array.isArray(item?.prices) ? item.prices[0] : null;
        return createComboSelectedItem(item, firstPrice);
      });
    });

    return defaults;
  };

  const getComboSelectionsFromCartItem = (cartItem) => {
    const restored = {};

    if (!Array.isArray(cartItem?.selectedGroups)) return restored;

    cartItem.selectedGroups.forEach((group) => {
      restored[String(group?.optionGroupId || "")] = Array.isArray(group?.items)
        ? group.items.map((item) => ({ ...item }))
        : [];
    });

    return restored;
  };

  const openComboConfigurator = (combo, cartIndex = null) => {
    if (!combo) return;

    setSelectedCombo(combo);
    setEditingComboCartIndex(cartIndex);

    if (cartIndex !== null && cart[cartIndex]) {
      setComboSelections(getComboSelectionsFromCartItem(cart[cartIndex]));
    } else {
      setComboSelections(
        String(combo?.isConfigurable || "N").toUpperCase() === "Y"
          ? {}
          : createDefaultComboSelections(combo),
      );
    }

    setShowComboModal(true);
  };

  const closeComboConfigurator = () => {
    setShowComboModal(false);
    setSelectedCombo(null);
    setComboSelections({});
    setEditingComboCartIndex(null);
  };

  const selectComboChoice = (group, item, priceObj) => {
    const groupKey = String(group?.optionGroupId || "");
    const maximum = Math.max(Number(group?.maximumSelection || 1), 1);
    const nextChoice = createComboSelectedItem(item, priceObj);

    setComboSelections((previous) => {
      const current = Array.isArray(previous[groupKey])
        ? [...previous[groupKey]]
        : [];

      if (maximum === 1) {
        return {
          ...previous,
          [groupKey]: [nextChoice],
        };
      }

      const existingIndex = current.findIndex(
        (choice) => String(choice?.itemId) === String(nextChoice.itemId),
      );

      if (existingIndex >= 0) {
        const updated = [...current];
        updated[existingIndex] = nextChoice;

        return {
          ...previous,
          [groupKey]: updated,
        };
      }

      if (current.length >= maximum) {
        toast.warn(`Maximum ${maximum} selection allowed`);
        return previous;
      }

      return {
        ...previous,
        [groupKey]: [...current, nextChoice],
      };
    });
  };

  const removeComboChoice = (group, itemId) => {
    const groupKey = String(group?.optionGroupId || "");

    setComboSelections((previous) => ({
      ...previous,
      [groupKey]: (previous[groupKey] || []).filter(
        (choice) => String(choice?.itemId) !== String(itemId),
      ),
    }));
  };

  const isComboChoiceSelected = (group, item, priceObj) => {
    const groupKey = String(group?.optionGroupId || "");
    const choices = comboSelections[groupKey] || [];
    const variantId =
      priceObj?.size_id === undefined ||
      priceObj?.size_id === null ||
      String(priceObj?.size_id || "").trim() === ""
        ? null
        : String(priceObj.size_id);

    return choices.some(
      (choice) =>
        String(choice?.itemId) === String(item?.item_id || item?.itemId) &&
        String(choice?.variantId ?? "") === String(variantId ?? "") &&
        String(choice?.variantName || "") === String(priceObj?.size || ""),
    );
  };

  const buildComboCartItem = (combo, selections) => {
    const groups = Array.isArray(combo?.optionGroups) ? combo.optionGroups : [];

    const selectedGroups = groups
      .map((group) => {
        const selectedItems = Array.isArray(
          selections[String(group?.optionGroupId || "")],
        )
          ? selections[String(group?.optionGroupId || "")]
          : [];

        return {
          optionGroupId: Number(group?.optionGroupId || 0),
          comboId: Number(group?.comboId || combo?.comboId || 0),
          groupTitle: group?.groupTitle || "",
          minimumSelection: Number(group?.minimumSelection || 0),
          maximumSelection: Number(group?.maximumSelection || 0),
          isItemConfigurable: group?.isItemConfigurable || "N",
          items: selectedItems.map((item) => {
            const productId = String(item?.productId || item?.itemId || "");
            const productName =
              item?.productName || item?.actualName || item?.itemName || "";
            const sizeWords = item?.sizeWords || item?.variantName || "";
            const rawSizeId = item?.sizeId || item?.variantId || "";
            const sizeId = String(rawSizeId || productId);

            return {
              // Keep the exact POS-item data in cart so PaymentModel can reuse it.
              productId,
              sizeId,
              productName,
              actualName: item?.actualName || productName,
              customization:
                typeof item?.customization === "string" &&
                item.customization.trim()
                  ? item.customization
                  : "{}",
              hasVarient: item?.hasVarient || sizeWords || "false",
              itemImage: item?.itemImage || "",
              itemPrice: String(item?.itemPrice ?? item?.price ?? 0),
              quantity: String(item?.quantity || 1),
              sizeWords,

              // UI compatibility aliases
              itemId: productId,
              itemName: productName,
              variantId:
                item?.variantId === undefined ||
                item?.variantId === null ||
                String(item?.variantId || "").trim() === ""
                  ? null
                  : String(item.variantId),
              variantName: sizeWords,
            };
          }),
        };
      })
      .filter((group) => group.items.length > 0);

    const comboPrice = Number(combo?.comboPrice || combo?.price || 0);
    const comboId = Number(combo?.comboId || 0);

    return {
      // Required combo order-line schema
      schemaVersion: 1,
      lineType: "COMBO",
      comboId,
      comboName: combo?.comboName || combo?.item_name || "Combo",
      comboType: combo?.comboType || "",
      isConfigurable: combo?.isConfigurable || "N",
      comboDescription:
        combo?.comboDescription || combo?.item_description || "",
      comboPrice,
      comboImage: combo?.comboImage || combo?.photo || "",
      selectedGroups,

      // UI/cart compatibility only. These fields are not used in combo payload.
      item_id: `combo_${comboId}`,
      item_name: combo?.comboName || combo?.item_name || "Combo",
      item_description:
        combo?.comboDescription || combo?.item_description || "",
      image: combo?.comboImage || combo?.photo || "",
      photo: combo?.comboImage || combo?.photo || "",
      price: comboPrice,
      qty: 1,
      selectedPrice: {
        size_id: `combo_${comboId}`,
        size: "Combo",
        price: comboPrice,
      },
      optionGroups: groups,
    };
  };

  const isComboSelectionValid = (combo, selections = comboSelections) => {
    const groups = Array.isArray(combo?.optionGroups) ? combo.optionGroups : [];

    return groups.every((group) => {
      const selectedCount = Array.isArray(
        selections[String(group?.optionGroupId || "")],
      )
        ? selections[String(group?.optionGroupId || "")].length
        : 0;

      const minimum = Math.max(Number(group?.minimumSelection || 0), 0);
      const maximum = Math.max(Number(group?.maximumSelection || 0), minimum);

      return selectedCount >= minimum && selectedCount <= maximum;
    });
  };

  const saveComboToCart = (combo, selections = comboSelections) => {
    if (!combo) return;

    if (!isComboSelectionValid(combo, selections)) {
      toast.warn("Please complete all required combo selections");
      return;
    }

    const preparedCombo = buildComboCartItem(combo, selections);

    setCart((previous) => {
      if (editingComboCartIndex !== null && previous[editingComboCartIndex]) {
        return previous.map((item, index) =>
          index === editingComboCartIndex
            ? {
                ...preparedCombo,
                qty: Number(previous[editingComboCartIndex]?.qty || 1),
              }
            : item,
        );
      }

      const preparedKey = getItemKey(preparedCombo);
      const existingIndex = previous.findIndex(
        (item) => getItemKey(item) === preparedKey,
      );

      if (existingIndex >= 0) {
        return previous.map((item, index) =>
          index === existingIndex
            ? { ...item, qty: Number(item?.qty || 1) + 1 }
            : item,
        );
      }

      return [...previous, preparedCombo];
    });

    closeComboConfigurator();
  };

  const normalizeComboSelectedItemForPayload = (selectedItem) => {
    const productId = String(
      selectedItem?.productId || selectedItem?.itemId || "",
    );

    const productName =
      selectedItem?.productName ||
      selectedItem?.actualName ||
      selectedItem?.itemName ||
      "";

    const sizeWords =
      selectedItem?.sizeWords || selectedItem?.variantName || "";

    const rawSizeId = selectedItem?.sizeId || selectedItem?.variantId || "";

    const sizeId = String(rawSizeId || productId);

    const rawCustomization = selectedItem?.customization;
    const customization =
      typeof rawCustomization === "string" && rawCustomization.trim()
        ? rawCustomization
        : "{}";

    return {
      productId,
      sizeId,
      productName,
      actualName: selectedItem?.actualName || productName,
      customization,
      hasVarient: selectedItem?.hasVarient || sizeWords || "false",
      itemImage:
        selectedItem?.itemImage ||
        selectedItem?.image ||
        selectedItem?.photo ||
        "",
      itemPrice: String(selectedItem?.itemPrice ?? selectedItem?.price ?? 0),
      quantity: String(selectedItem?.quantity || 1),
      sizeWords,
    };
  };

  const normalizeComboSelectedGroupsForPayload = (groups) =>
    (Array.isArray(groups) ? groups : []).map((group) => ({
      ...group,
      items: (Array.isArray(group?.items) ? group.items : []).map(
        normalizeComboSelectedItemForPayload,
      ),
    }));

  const buildComboCustomizationPayload = (item) => ({
    schemaVersion: Number(item?.schemaVersion || 1),
    lineType: "COMBO",
    comboId: Number(item?.comboId || 0),
    comboName: item?.comboName || item?.item_name || "Combo",
    comboType: item?.comboType || "",
    isConfigurable: item?.isConfigurable || "N",
    comboDescription: item?.comboDescription || item?.item_description || "",
    comboPrice: Number(item?.comboPrice || item?.price || 0),
    comboImage: item?.comboImage || item?.image || item?.photo || "",
    selectedGroups: normalizeComboSelectedGroupsForPayload(
      item?.selectedGroups,
    ),
  });

  const buildOrderLine = (item) => {
    if (isComboLine(item)) {
      const comboPayload = buildComboCustomizationPayload(item);
      const comboId = String(comboPayload.comboId || "");
      const comboName = comboPayload.comboName || "Combo";
      const comboPrice = String(comboPayload.comboPrice || 0);
      const comboImage = comboPayload.comboImage || "";

      // IMPORTANT:
      // placePosOrder expects every root items[] entry in normal POS item shape.
      // The complete combo schema is sent inside customization as JSON string.
      return {
        productId: `${userID}/${comboId}`,
        sizeId: comboId,
        productName: comboName,
        actualName: comboName,
        sizeWords: "",
        quantity: String(item?.qty || item?.quantity || 1),
        itemPrice: comboPrice,
        itemImage: comboImage,
        customization: JSON.stringify(comboPayload),
        hasVarient: "N",
      };
    }

    return {
      productId: String(item.item_id),
      sizeId: String(
        item.selectedPrice?.size_id || item.size_id || item.item_id,
      ),
      productName: item.item_name,
      actualName: item.item_name,
      sizeWords: item.selectedPrice?.size || item.size || item.sizeWords || "",
      itemPrice: String(
        item.price || item.item_price || item.selectedPrice?.price || 0,
      ),
      quantity: String(item.qty || item.quantity || 1),
      customization: JSON.stringify(item.customizations || []),
      hasVarient: item.selectedPrice?.size_id ? "true" : "false",
      itemImage: item.image || item.photo || item.itemImage || "",
    };
  };

  const buildOrderLines = (items) => items.map(buildOrderLine);

  const getValidCart = () => {
    return cart.filter((item) => Number(item.qty || item.quantity || 0) > 0);
  };

  const selectedCartItem =
    selectedCartIndex !== null ? cart[selectedCartIndex] : null;

  const hasSelectedCartItem =
    selectedCartIndex !== null &&
    selectedCartIndex >= 0 &&
    selectedCartIndex < cart.length &&
    Number(selectedCartItem?.qty || 0) > 0;

  const handleHold = async () => {
    const validCart = getValidCart();

    if (validCart.length === 0) {
      toast.warn("Cart is empty");
      return;
    }

    const payload = {
      userid: userID,
      currency: "INR",
      custMobile: customerMobile || "919999999999",
      custName: customerName || "",
      orderId: orderId,
      orderType: payloadOrderType,
      tableNo: getCurrentTableNo(),
      roomNumber: isRoomOrder ? getCurrentTableNo() : "",
      paymentMode: "unpaid",
      orderStatus: "hold",
      items: buildOrderLines(validCart),
    };

    try {
      const result = await placePosOrder(payload);

      if (result?.responseCode === "00") {
        toast.success("Order put on hold");
        await refreshDiningCache();
        setCart([]);

        await onHoldSuccess?.();

        onClose?.();
      }
    } catch (e) {
      console.error("Hold failed:", e);
      toast.error("Hold failed");
    }
  };

  const handlePaymentClick = async () => {
    try {
      const validCart = getValidCart();

      if (validCart.length === 0) {
        toast.warn("Cart is empty");
        return;
      }

      let finalOrderId = activeOrderId || orderId;

      if (!finalOrderId) {
        const id = await generateOrderID({
          mobile: customerMobile || "9999999999",
          orderType: payloadOrderType,
        });

        if (!id) {
          toast.error("Order ID generation failed");
          return;
        }

        finalOrderId = id;
        setActiveOrderId(id);
        setKotOrderId(id);
      }

      const payload = {
        userid: userID,
        currency: "INR",
        custMobile: customerMobile || "919999999999",
        custName: customerName || "Default Customer",

        orderId: finalOrderId,
        orderType: payloadOrderType,
        orderStatus: "hold",

        paymentMode: "unpaid",
        paymentType: "",

        tableNo: getCurrentTableNo(),
        roomNumber: isRoomOrder ? getCurrentTableNo() : "",

        deliveryAmount: "0",
        deliveryChargesDeductedFrom: "customer",
        deliveryPartner: "",

        items: buildOrderLines(validCart),
      };

      const res = await placePosOrder(payload);

      if (res?.responseCode !== "00") {
        toast.error(res?.responseMessage || "Order create failed");
        return;
      }

      setActiveOrderId(finalOrderId);
      setKotOrderId(finalOrderId);
      setShowPayment(true);
    } catch (error) {
      console.error("Payment click error:", error);
      toast.error("Something went wrong");
    }
  };

  const handleKotClick = async () => {
    const validCart = getValidCart();

    if (validCart.length === 0) {
      toast.warn("Cart is empty");
      return;
    }

    const cartSnapshot = validCart.map((item, index) => {
      const qty = Number(item?.qty || item?.quantity || 1);

      const unitPrice = Number(
        item?.price ||
          item?.item_price ||
          item?.selectedPrice?.price ||
          item?.prices?.[0]?.price ||
          0,
      );

      const itemName =
        item?.item_name ||
        item?.itemName ||
        item?.productName ||
        item?.actualName ||
        item?.name ||
        `Item ${index + 1}`;

      let customizations = [];

      if (Array.isArray(item?.customizations)) {
        customizations = item.customizations;
      } else if (Array.isArray(item?.customization)) {
        customizations = item.customization;
      } else if (
        typeof item?.customization === "string" &&
        item.customization.trim()
      ) {
        try {
          const parsed = JSON.parse(item.customization);

          customizations = Array.isArray(parsed)
            ? parsed
            : parsed
              ? [parsed]
              : [];
        } catch (error) {
          console.error("KOT customization parse error:", error);
          customizations = [];
        }
      }

      return {
        ...item,

        id:
          item?.id ||
          item?.item_id ||
          item?.productId ||
          item?.product_id ||
          `${index}`,

        item_id:
          item?.item_id ||
          item?.itemId ||
          item?.productId ||
          item?.product_id ||
          `${index}`,

        productId:
          item?.productId || item?.product_id || item?.item_id || `${index}`,

        item_name: itemName,
        itemName,
        productName: itemName,
        actualName: itemName,

        quantity: qty,
        qty,

        item_price: unitPrice,
        itemPrice: unitPrice,
        price: unitPrice,

        total_price: unitPrice * qty,
        totalPrice: unitPrice * qty,

        customization: JSON.stringify(customizations),
        customizations,

        selectedPrice: {
          ...(item?.selectedPrice || {}),
          size_id:
            item?.selectedPrice?.size_id ||
            item?.size_id ||
            item?.item_id ||
            "",
          price: unitPrice,
          size:
            item?.selectedPrice?.size || item?.sizeWords || item?.size || "",
        },
      };
    });

    const snapshotSubtotal = cartSnapshot.reduce((sum, item) => {
      return sum + Number(item.total_price || 0);
    }, 0);

    // API calls se pehle snapshot store karo
    setKotCartSnapshot(cartSnapshot);

    try {
      setKotLoading(true);

      let finalOrderId = activeOrderId || orderId || kotOrderId;

      if (!finalOrderId) {
        const id = await generateOrderID({
          mobile: customerMobile || "9999999999",
          orderType: payloadOrderType,
        });

        if (!id) {
          toast.error("Order ID generation failed");
          return;
        }

        finalOrderId = id;
      }

      setKotOrderId(finalOrderId);

      const payload = {
        userid: userID,
        currency: "INR",
        custMobile: customerMobile || "919999999999",
        custName: customerName || "Default Customer",
        orderId: finalOrderId,
        orderType: payloadOrderType,
        orderStatus: "hold",
        paymentMode: "unpaid",
        tableNo: getCurrentTableNo(),
        roomNumber: isRoomOrder ? getCurrentTableNo() : "",

        deliveryAmount: "0",
        deliveryChargesDeductedFrom: "customer",
        deliveryPartner: "",

        items: buildOrderLines(cartSnapshot),
      };

      console.log("KOT CART SNAPSHOT:", cartSnapshot);
      console.log("KOT PLACE PAYLOAD:", payload);

      const placeRes = await placePosOrder(payload);

      console.log("KOT PLACE RESPONSE:", placeRes);

      if (placeRes?.responseCode !== "00") {
        toast.error(placeRes?.responseMessage || "KOT order create failed");
        return;
      }

      // Modal ko API detail response par depend nahi rakhenge
      const fallbackKotOrder = {
        orderid: finalOrderId,
        orderId: finalOrderId,

        customer_mobile: customerMobile || "9999999999",

        custMobile: customerMobile || "9999999999",

        customer_name: customerName || "Default Customer",

        orderType: payloadOrderType,

        table_no: getCurrentTableNo(),
        tableNo: getCurrentTableNo(),

        roomNumber: isRoomOrder ? getCurrentTableNo() : "",

        items: cartSnapshot,
        orderItems: cartSnapshot,
        order_items: cartSnapshot,
        cart: cartSnapshot,

        subtotal: snapshotSubtotal,
        discount_amount: 0,
        totalOrderPrice: snapshotSubtotal,
        order_amount: snapshotSubtotal,
      };

      // Pehle guaranteed cart data ke saath modal open karo
      setKotOrderData(fallbackKotOrder);
      setShowKotModal(true);

      // Print API failure se modal affect nahi hoga
      try {
        await printKot(finalOrderId);
      } catch (printError) {
        console.error("Print KOT API error:", printError);
      }

      // API details available hain to basic information merge karo,
      // lekin items hamesha snapshot se rakho
      try {
        const details = await getOrderDetails(finalOrderId);

        console.log("KOT DETAILS RESPONSE:", details);

        let fullOrder = null;

        if (Array.isArray(details)) {
          fullOrder = details[0] || null;
        } else if (Array.isArray(details?.response)) {
          fullOrder = details.response[0] || null;
        } else if (details?.response && typeof details.response === "object") {
          fullOrder = details.response;
        } else if (details && typeof details === "object") {
          fullOrder = details;
        }

        if (fullOrder) {
          setKotOrderData((previous) => ({
            ...(previous || fallbackKotOrder),
            ...fullOrder,

            orderid: fullOrder?.orderid || fullOrder?.orderId || finalOrderId,

            orderId: fullOrder?.orderId || fullOrder?.orderid || finalOrderId,

            customer_mobile:
              fullOrder?.customer_mobile ||
              fullOrder?.custMobile ||
              customerMobile ||
              "9999999999",

            orderType:
              fullOrder?.orderType || fullOrder?.order_type || payloadOrderType,

            table_no:
              fullOrder?.table_no || fullOrder?.tableNo || getCurrentTableNo(),

            tableNo:
              fullOrder?.tableNo || fullOrder?.table_no || getCurrentTableNo(),

            // Important: API ke empty items se overwrite nahi karna
            items: cartSnapshot,
            orderItems: cartSnapshot,
            order_items: cartSnapshot,
            cart: cartSnapshot,

            subtotal:
              Number(
                fullOrder?.subtotal || fullOrder?.subTotal || snapshotSubtotal,
              ) || snapshotSubtotal,

            discount_amount:
              Number(
                fullOrder?.discount_amount || fullOrder?.discountAmount || 0,
              ) || 0,

            totalOrderPrice:
              Number(
                fullOrder?.totalOrderPrice ||
                  fullOrder?.order_amount ||
                  snapshotSubtotal,
              ) || snapshotSubtotal,
          }));
        }
      } catch (detailsError) {
        console.error("Get KOT order details error:", detailsError);
      }
    } catch (err) {
      console.error("KOT flow failed:", err);
      toast.error("KOT generation failed");
    } finally {
      setKotLoading(false);
    }
  };

  const applyCustomization = () => {
    if (!hasSelectedCartItem) {
      toast.warn("Selected item not found");
      setSelectedCartIndex(null);
      setSelectedOptions({});
      setShowCustomize(false);
      return;
    }

    const updatedCart = [...cart];
    const currentItem = updatedCart[selectedCartIndex];

    if (!currentItem || Number(currentItem.qty || 0) <= 0) {
      toast.warn("Selected item not found");
      setSelectedCartIndex(null);
      setSelectedOptions({});
      setShowCustomize(false);
      return;
    }

    const customizations = [];

    if (selectedOptions.type) {
      customizations.push({
        customization_type: "customize",
        customize_field: "type",
        options: [selectedOptions.type],
      });
    }

    if (selectedOptions.addons?.length > 0) {
      customizations.push({
        customization_type: "customize",
        customize_field: "addons",
        options: selectedOptions.addons,
      });
    }

    updatedCart[selectedCartIndex] = {
      ...currentItem,
      customizations,
    };

    setCart(updatedCart);
    setSelectedOptions({});
    setShowCustomize(false);
  };

  const items = activeCategoryData?.menu_items || [];
  const safeItems = Array.isArray(items) ? items : [];
  const safeArray = (arr) => (Array.isArray(arr) ? arr : []);

  const filteredItems = useMemo(() => {
    if (!normalizedSearchItem) return safeItems;

    const activeCategoryMatchesSearch = String(activeCategory || "")
      .toLowerCase()
      .includes(normalizedSearchItem);

    // Category search: show every item from the matched category.
    if (activeCategoryMatchesSearch) {
      return safeItems;
    }

    // Normal item search stays unchanged.
    return safeItems.filter((item) =>
      String(item?.item_name || item?.comboName || "")
        .toLowerCase()
        .includes(normalizedSearchItem),
    );
  }, [safeItems, normalizedSearchItem, activeCategory]);

  const addToCart = (item, selectedPrice) => {
    if (isComboLine(item)) {
      const groups = Array.isArray(item?.optionGroups) ? item.optionGroups : [];

      if (
        String(item?.isConfigurable || "N").toUpperCase() === "Y" &&
        groups.length > 0
      ) {
        openComboConfigurator(item);
        return;
      }

      const defaults = createDefaultComboSelections(item);
      saveComboToCart(item, defaults);
      return;
    }

    const priceObj = selectedPrice || item.prices?.[0];

    if (!priceObj) {
      toast.warn("Price not found for this item");
      return;
    }

    const itemKey = `${item.item_id}-${
      priceObj.size_id || priceObj.price || item.item_id
    }`;

    setCart((prev) => {
      const existing = prev.find(
        (c) =>
          `${c.item_id}-${
            c.selectedPrice?.size_id || c.selectedPrice?.price || c.item_id
          }` === itemKey,
      );

      if (existing) {
        return prev.map((c) =>
          `${c.item_id}-${
            c.selectedPrice?.size_id || c.selectedPrice?.price || c.item_id
          }` === itemKey
            ? { ...c, qty: Number(c.qty || 1) + 1 }
            : c,
        );
      }

      return [
        ...prev,
        {
          ...item,
          qty: 1,
          selectedPrice: priceObj,
          price: Number(priceObj.price || 0),
        },
      ];
    });
  };

  const increaseCartItem = (item) => {
    if (isComboLine(item)) {
      const itemKey = getItemKey(item);

      setCart((previous) =>
        previous.map((cartItem) =>
          getItemKey(cartItem) === itemKey
            ? { ...cartItem, qty: Number(cartItem?.qty || 1) + 1 }
            : cartItem,
        ),
      );

      return;
    }

    addToCart(item, item.selectedPrice);
  };

  const removeFromCart = (item) => {
    const itemKey = getItemKey(item);

    setCart((prev) => {
      const existingIndex = prev.findIndex((c) => getItemKey(c) === itemKey);

      if (existingIndex === -1) return prev;

      const existing = prev[existingIndex];

      // qty 1 hai to item remove hoga, 0 nahi banega
      if (Number(existing.qty) <= 1) {
        const updated = prev.filter((c) => getItemKey(c) !== itemKey);

        // selected item remove ho gaya to selection clear
        if (selectedCartIndex === existingIndex) {
          setSelectedCartIndex(null);
          setSelectedOptions({});
          setShowCustomize(false);
        }

        // agar removed index ke baad selection shift karni ho
        if (selectedCartIndex !== null && selectedCartIndex > existingIndex) {
          setSelectedCartIndex(selectedCartIndex - 1);
        }

        return updated;
      }

      return prev.map((c) =>
        getItemKey(c) === itemKey
          ? { ...c, qty: Math.max(Number(c.qty || 1) - 1, 1) }
          : c,
      );
    });
  };

  const clearCart = () => {
    setCart([]);
    setSelectedCartIndex(null);
    setSelectedOptions({});
    setShowCustomize(false);
    closeComboConfigurator();
  };

  const toNum = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const getOrgTaxStorageKey = (userid) => `pos_org_tax_config_${userid}`;

  const getStoredTaxConfig = () => {
    try {
      const raw = sessionStorage.getItem(getOrgTaxStorageKey(userID));
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      console.error("DineScreen tax config parse error:", error);
      return {};
    }
  };

  const cartSubtotal = cart.reduce((sum, c) => {
    return sum + Number(c.price || 0) * Number(c.qty || 0);
  }, 0);

  const storedTaxConfig = getStoredTaxConfig();

  const gstType =
    storedTaxConfig?.gst_type || storedTaxConfig?.gstType || "excl";

  const normalizedGstType = String(gstType || "")
    .toLowerCase()
    .trim();

  const isInclusiveGst =
    normalizedGstType === "incl" || normalizedGstType === "inclusive";

  const roundAmount = (value) => {
    const num = Number(value || 0);

    if (!Number.isFinite(num)) return 0;

    return Math.ceil(num);
  };

  // Hardcoded 5% GST included formula.
  // Same concept used in other files:
  // Total GST = amount - amount / 1.05
  // CGST = Total GST / 2
  // SGST = Total GST / 2
  const getHardcodedTaxAmounts = (amount) => {
    const taxableAmount = Math.max(Number(amount || 0), 0);

    const includedTotalTax = taxableAmount - taxableAmount / 1.05;

    return {
      cgst: includedTotalTax / 2,
      sgst: includedTotalTax / 2,
    };
  };

  const dineTaxAmounts = getHardcodedTaxAmounts(cartSubtotal);

  const dineCgstAmount = dineTaxAmounts.cgst;
  const dineSgstAmount = dineTaxAmounts.sgst;

  const calculatedTotal = isInclusiveGst
    ? cartSubtotal
    : cartSubtotal + dineCgstAmount + dineSgstAmount;

  const total = roundAmount(calculatedTotal);

  useEffect(() => {
    if (!barcodeInput.trim()) return;

    const timer = setTimeout(() => {
      handleBarcodeSubmit();
    }, 350);

    return () => clearTimeout(timer);
  }, [barcodeInput]);

  useEffect(() => {
    if (selectedCartIndex !== null && selectedCartIndex >= cart.length) {
      setSelectedCartIndex(null);
      setSelectedOptions({});
      setShowCustomize(false);
    }

    if (cart.length === 0) {
      setSelectedCartIndex(null);
      setSelectedOptions({});
      setShowCustomize(false);
    }
  }, [cart, selectedCartIndex]);

  useEffect(() => {
    setActiveOrderId(orderId || "");
  }, [orderId]);

  useEffect(() => {
    if (Array.isArray(existingItems) && existingItems.length > 0) {
      const preloaded = existingItems.map((item) => {
        if (String(item?.lineType || "").toUpperCase() === "COMBO") {
          const comboId = Number(item?.comboId || item?.combo_id || 0);
          const comboPrice = Number(
            item?.comboPrice || item?.combo_price || item?.price || 0,
          );

          return {
            ...item,
            schemaVersion: Number(item?.schemaVersion || 1),
            lineType: "COMBO",
            comboId,
            comboName:
              item?.comboName || item?.combo_name || item?.item_name || "Combo",
            comboType: item?.comboType || item?.combo_type || "",
            isConfigurable:
              item?.isConfigurable || item?.is_configurable || "N",
            comboDescription:
              item?.comboDescription ||
              item?.combo_description ||
              item?.item_description ||
              "",
            comboPrice,
            comboImage:
              item?.comboImage ||
              item?.combo_image ||
              item?.itemImage ||
              item?.image ||
              "",
            selectedGroups: Array.isArray(item?.selectedGroups)
              ? item.selectedGroups
              : [],
            item_id: `combo_${comboId}`,
            item_name:
              item?.comboName || item?.combo_name || item?.item_name || "Combo",
            qty: Number(item?.quantity || item?.qty || 1),
            price: comboPrice,
            image:
              item?.comboImage ||
              item?.combo_image ||
              item?.itemImage ||
              item?.image ||
              "",
            selectedPrice: {
              size_id: `combo_${comboId}`,
              price: comboPrice,
              size: "Combo",
            },
          };
        }

        return {
          item_id: item.item_id,
          item_name: item.item_name,
          qty: Number(item.quantity || item.qty || 1),
          price: Number(item.item_price || item.price || 0),

          selectedPrice: {
            size_id: item.size_id || item.item_id,
            price: item.item_price || item.price || 0,
            size: item.sizeWords || item.size || "",
          },

          size: item.sizeWords || "Full Plate",
          image: item.image || item.itemImage || "",
          customizations: (() => {
            if (Array.isArray(item.customizations)) return item.customizations;

            try {
              return item.customization ? JSON.parse(item.customization) : [];
            } catch {
              return [];
            }
          })(),
        };
      });

      setCart(preloaded);
    }
  }, [existingItems]);

  if (showPayment) {
    return (
      <div className="h-full w-full bg-gray-100">
        <PaymentModal
          cart={getValidCart()}
          total={cartSubtotal}
          orderId={activeOrderId || orderId}
          reciptDetails={reciptDetails}
          customerMobile={customerMobile}
          orderType={payloadOrderType}
          selectedTable={{
            tableNo: getCurrentTableNo(),
            table_no: getCurrentTableNo(),

            roomNumber: isRoomOrder ? getCurrentTableNo() : "",

            roomNo: isRoomOrder ? getCurrentTableNo() : "",

            room_no: isRoomOrder ? getCurrentTableNo() : "",
          }}
          selectedRoom={
            isRoomOrder
              ? {
                  roomNumber: getCurrentTableNo(),
                  roomNo: getCurrentTableNo(),
                  room_no: getCurrentTableNo(),
                }
              : null
          }
          onAfterOrderSuccess={async () => {
            setCart([]);
            setActiveOrderId("");

            await onHoldSuccess?.();
          }}
          onClose={(action) => {
            setShowPayment(false);

            if (action === "home") {
              setCart([]);
              setActiveOrderId("");
              onClose?.();
            }
          }}
        />
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full font-sans text-sm select-none montserrat-font-1 overflow-hidden">
        {/*  LEFT COLUMN — Cart + Payment button */}
        <div className="w-80 flex-shrink-0 bg-white flex flex-col border-r border-gray-200">
          {/* Barcode input */}
          <div className="px-2 py-2 border-b border-gray-200">
            <input
              ref={searchRef}
              type="text"
              placeholder="Scan or search by barcode / item ID"
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleBarcodeSubmit();
                }
              }}
              className="w-full border border-blue-400 rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-300 placeholder-gray-400"
            />
          </div>

          {/* Cart items */}
          <div className="flex-1 overflow-y-auto">
            {cart.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-xs">
                Cart is empty
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0 border-b border-gray-200">
                  <tr className="text-gray-500">
                    <th className="text-left px-2 py-2 font-medium">Item</th>
                    <th className="text-center px-2 py-2 font-medium">Qty</th>
                    <th className="text-right px-3 py-2 font-medium">Amt</th>
                  </tr>
                </thead>
                <tbody>
                  {safeArray(cart).map((item, index) => (
                    <tr
                      key={getItemKey(item)}
                      onClick={() => setSelectedCartIndex(index)}
                      className={`border-b border-gray-100 cursor-pointer ${
                        selectedCartIndex === index ? "bg-blue-100" : ""
                      }`}
                    >
                      {/* ITEM NAME */}
                      <td className="px-2 py-2 font-medium text-gray-700 text-[14px] leading-tight">
                        {item.item_name}

                        {isComboLine(item) &&
                          Array.isArray(item?.selectedGroups) &&
                          item.selectedGroups.length > 0 && (
                            <div className="mt-1 space-y-0.5 text-[10px] text-gray-500">
                              {item.selectedGroups.map((group) => (
                                <div key={group.optionGroupId}>
                                  <span className="font-semibold">
                                    {group.groupTitle}:
                                  </span>{" "}
                                  {(group.items || [])
                                    .map(
                                      (selectedItem) =>
                                        `${selectedItem.itemName}${
                                          selectedItem.variantName
                                            ? ` (${selectedItem.variantName})`
                                            : ""
                                        }`,
                                    )
                                    .join(", ")}
                                </div>
                              ))}
                            </div>
                          )}

                        {!isComboLine(item) &&
                          item.customizations?.length > 0 && (
                            <div className="text-[12px] text-gray-600 mt-1">
                              {item.customizations
                                .flatMap((c) => c.options)
                                .join(", ")}
                            </div>
                          )}
                      </td>

                      {/* QTY CONTROLS */}
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromCart(item);
                            }}
                            className="w-6 h-6 rounded bg-red-100 text-red-600 font-bold hover:bg-red-200 flex items-center justify-center"
                          >
                            −
                          </button>

                          <span className="w-6 text-center font-semibold text-gray-800">
                            {item.qty}
                          </span>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              increaseCartItem(item);
                            }}
                            className="w-6 h-6 rounded bg-green-100 text-green-600 font-bold hover:bg-green-200 flex items-center justify-center"
                          >
                            +
                          </button>
                        </div>
                      </td>

                      {/* AMOUNT */}
                      <td className="px-3 py-2 text-right font-semibold text-gray-800">
                        ₹{(item.price * item.qty).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Total row */}
          <div className="border-t border-gray-200 px-4 py-2 flex justify-between items-center bg-gray-50">
            <span className="font-bold text-gray-800 text-base">Total</span>

            <span className="font-bold text-gray-800 text-base">₹ {total}</span>
          </div>

          {/* LEFT bottom: Payment only */}
          <button
            onClick={handlePaymentClick}
            className="bg-emerald-500 hover:bg-emerald-900 active:bg-gray-800 text-white font-bold mb-2 p-3.5 mx-4 rounded-lg text-sm transition-colors tracking-wide border-t border-gray-200 flex-shrink-0"
          >
            Payment
          </button>
        </div>

        {/* CENTER COLUMN */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Items panel */}
          <div className="flex-1 flex flex-col bg-white mx-2 mt-2 rounded-t-lg shadow-sm overflow-hidden relative">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 flex-shrink-0">
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold text-gray-800">
                  {activeCategory}
                </h2>

                {normalizedSearchItem && matchingCategories.length > 0 && (
                  <p className="mt-0.5 text-[10px] font-medium text-emerald-600">
                    Category match · showing all items
                  </p>
                )}
              </div>
              <input
                type="text"
                placeholder="Search item or category..."
                value={searchItem}
                onChange={(e) => setSearchItem(e.target.value)}
                className="border border-blue-400 rounded px-3 py-2.5 text-xs w-60 outline-none focus:ring-2 focus:ring-blue-300 placeholder-gray-400"
              />
            </div>

            {/* Items grid */}
            <div className="flex-1 overflow-y-auto p-3">
              {catalogLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center gap-2 text-gray-400">
                    <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs">Loading items...</span>
                  </div>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400 text-xs">
                  No items found
                </div>
              ) : (
                <div className="grid grid-cols-6 gap-2">
                  {filteredItems.map((item) => {
                    const isCombo = isComboLine(item);

                    const comboQty = isCombo
                      ? cart
                          .filter(
                            (cartItem) =>
                              isComboLine(cartItem) &&
                              Number(cartItem?.comboId || 0) ===
                                Number(item?.comboId || 0),
                          )
                          .reduce(
                            (sum, cartItem) => sum + Number(cartItem?.qty || 0),
                            0,
                          )
                      : 0;

                    const inCart = isCombo
                      ? comboQty > 0
                      : cart.find(
                          (cartItem) => cartItem.item_id === item.item_id,
                        );

                    if (isCombo) {
                      return (
                        <button
                          key={`combo-${item.comboId}`}
                          onClick={() => addToCart(item, item.prices?.[0])}
                          className={`relative col-span-2 flex min-h-[178px] flex-col overflow-hidden rounded-lg border text-left transition-all hover:shadow-md active:scale-[0.99] ${
                            inCart
                              ? "border-emerald-400 bg-emerald-50"
                              : "border-violet-300 bg-violet-50 hover:border-violet-500"
                          }`}
                        >
                          <div className="relative h-24 w-full overflow-hidden border-b border-violet-100 bg-white">
                            {item.comboImage ? (
                              <img
                                src={item.comboImage}
                                alt={item.comboName || "Combo"}
                                className="h-full w-full object-contain p-1"
                                loading="lazy"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-gray-50 text-[11px] font-semibold text-gray-400">
                                Combo image unavailable
                              </div>
                            )}
                          </div>

                          <div className="flex flex-1 flex-col p-2">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="text-[12px] font-bold leading-tight text-gray-900">
                                  {item.comboName || item.item_name}
                                </div>
                                <div className="mt-0.5 line-clamp-1 text-[9px] leading-3 text-gray-500">
                                  {item.comboDescription ||
                                    item.item_description}
                                </div>
                              </div>

                              <span className="shrink-0 rounded-md bg-white px-1.5 py-0.5 text-[11px] font-black text-violet-700 shadow-sm">
                                ₹{Number(item.comboPrice || 0)}
                              </span>
                            </div>

                            <div className="mt-auto flex items-center justify-between pt-1.5">
                              <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[8px] font-bold uppercase tracking-wide text-violet-700">
                                {String(
                                  item.isConfigurable || "N",
                                ).toUpperCase() === "Y"
                                  ? "Configure Combo"
                                  : "Add Combo"}
                              </span>

                              {comboQty > 0 && (
                                <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[8px] font-bold text-white">
                                  {comboQty} in cart
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    }

                    return (
                      <button
                        key={item.item_id}
                        onClick={() => {
                          if (!item.prices || item.prices.length === 1) {
                            addToCart(item, item.prices?.[0]);
                          }
                        }}
                        className={`relative px-2 py-3 rounded border text-xs font-medium text-center transition-all hover:shadow-sm active:scale-95 ${
                          inCart
                            ? "border-emerald-400 text-emerald-700"
                            : "border-gray-800 bg-blue-100 text-gray-700 hover:border-blue-400 hover:bg-blue-300"
                        }`}
                      >
                        <div className="leading-tight block">
                          <div className="text-[13px] font-medium text-gray-800">
                            {item.item_name}
                          </div>

                          {/* Variant / Price */}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {item.prices?.length > 1 ? (
                              item.prices.map((price) => (
                                <button
                                  key={`${item.item_id}-${
                                    price.size_id || price.price
                                  }`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    addToCart(item, price);
                                  }}
                                  className="px-3 py-2 text-[14px] font-semibold border border-blue-300 bg-gray-100 text-blue-800 rounded-lg hover:bg-blue-200 transition shadow-sm"
                                >
                                  {price.price}
                                </button>
                              ))
                            ) : (
                              <span className="px-3 py-2 text-[14px] font-semibold border border-gray-300 bg-gray-100 text-gray-700 rounded-md">
                                ₹{item.prices?.[0]?.price}
                              </span>
                            )}
                          </div>
                        </div>

                        {inCart && (
                          <span className="absolute -top-1.5 -right-1.5 bg-emerald-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold shadow">
                            {inCart.qty}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Watermark */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <span className="text-8xl font-black text-gray-400 select-none tracking-widest opacity-20">
                Anlook POS
              </span>
            </div>
          </div>

          {/* CENTER bottom: 5 buttons in one row */}
          <div className="flex shadow-sm rounded-lg overflow-hidden flex-shrink-0 gap-3 mb-2 mx-3 mt-2">
            <button
              onClick={() => setShowCustomerModal(true)}
              className="flex-1 bg-gray-600 hover:bg-gray-700 active:bg-gray-800 text-white font-bold py-4 text-xs transition-colors tracking-wide"
            >
              Customer
            </button>
            <button
              onClick={clearCart}
              className="flex-1 bg-red-500 hover:bg-red-600 active:bg-red-700 text-white font-bold py-4 text-xs transition-colors tracking-wide border-l border-white/30"
            >
              Clear
            </button>
            <button
              onClick={() => {
                if (!hasSelectedCartItem) {
                  toast.warn("Select item first");
                  setSelectedCartIndex(null);
                  setSelectedOptions({});
                  return;
                }

                if (isComboLine(selectedCartItem)) {
                  openComboConfigurator(selectedCartItem, selectedCartIndex);
                  return;
                }

                const itemCustomizations =
                  selectedCartItem?.customizations || [];

                const typeCustomization = itemCustomizations.find(
                  (c) => c.customize_field === "type",
                );

                const addonCustomization = itemCustomizations.find(
                  (c) => c.customize_field === "addons",
                );

                setSelectedOptions({
                  type: typeCustomization?.options?.[0] || "",
                  addons: addonCustomization?.options || [],
                });

                setShowCustomize(true);
              }}
              className="flex-1 bg-cyan-400 hover:bg-cyan-500 active:bg-cyan-600 text-white font-bold py-4 text-xs transition-colors tracking-wide border-l border-white/30"
            >
              Customize
            </button>
            <button
              onClick={handleHold}
              className="flex-1 bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 text-white font-bold py-4 text-xs transition-colors tracking-wide border-l border-white/30"
            >
              Hold
            </button>

            <button
              onClick={handleKotClick}
              disabled={kotLoading}
              className="flex-1 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 disabled:bg-blue-300 text-white font-bold py-4 text-xs transition-colors tracking-wide border-l border-white/30"
            >
              {kotLoading ? "Generating..." : "KOT"}
            </button>
          </div>
        </div>

        {/* ═══════════════════════════════════════
          RIGHT COLUMN — Categories + Cancel btn
      ═══════════════════════════════════════ */}
        <div className="w-72 flex-shrink-0 bg-white border-l border-gray-200 flex flex-col">
          {/* Header */}
          <div className="px-3 py-2.5 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-sm font-semibold text-gray-700 text-center">
              Category
            </h3>
          </div>

          {/* Category list */}
          <div className="flex-1 overflow-y-auto py-2 px-2">
            {catalogLoading ? (
              <div className="flex justify-center pt-6">
                <div className="w-5 h-5 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {safeArray(displayedCategories).map((cat, index) => {
                  const isSearchMatch =
                    normalizedSearchItem &&
                    String(cat || "")
                      .toLowerCase()
                      .includes(normalizedSearchItem);

                  return (
                    <button
                      key={cat}
                      onClick={() => {
                        setActiveCategory(cat);
                        setSearchItem("");
                      }}
                      className={`relative w-full text-left px-4 py-3 text-sm font-medium rounded-md transition-colors border ${
                        activeCategory === cat
                          ? "bg-emerald-500 text-white border-emerald-500"
                          : isSearchMatch
                            ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                            : "text-gray-700 hover:bg-gray-100 border-gray-200 bg-white"
                      }`}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate">{cat}</span>

                        {isSearchMatch && index === 0 && (
                          <span
                            className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold ${
                              activeCategory === cat
                                ? "bg-white/20 text-white"
                                : "bg-emerald-100 text-emerald-700"
                            }`}
                          >
                            Search
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT bottom: Cancel only */}
          <button
            type="button"
            onClick={handleCancel}
            className="bg-gray-600 hover:bg-black active:bg-gray-800 text-white font-bold mb-2 p-3.5 mx-4 rounded-lg text-sm transition-colors tracking-wide border-t border-gray-200 flex-shrink-0"
          >
            Cancel
          </button>
        </div>

        {/* mobile number model */}
        <MobileNumberModal
          open={showCustomerModal}
          onClose={() => setShowCustomerModal(false)}
          orderType={orderType}
          initialMobile={customerMobile}
          initialName={customerName}
          onSubmit={(data) => {
            setCustomerMobile(normalizeMobile(data.mobile));
            setCustomerName(data.name);
          }}
        />

        <KotGeneratedModal
          open={showKotModal}
          order={kotOrderData}
          cart={kotCartSnapshot}
          orderId={kotOrderId || orderId}
          customerMobile={customerMobile}
          orderType={payloadOrderType}
          reciptDetails={reciptDetails}
          onClose={() => {
            setShowKotModal(false);
            setKotOrderData(null);
            setKotCartSnapshot([]);
            onClose?.();
          }}
          onDirectPrint={async () => {
            const finalPrintOrderId = kotOrderId || orderId;

            if (!finalPrintOrderId) {
              toast.error("Order ID not found");
              return;
            }

            await printKot(finalPrintOrderId);
          }}
        />

        {/* Combo configurator */}
        {showComboModal && selectedCombo && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4">
            <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
                <div className="flex min-w-0 items-start gap-4">
                  {selectedCombo?.comboImage && (
                    <div className="h-24 w-32 shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-white p-1">
                      <img
                        src={selectedCombo.comboImage}
                        alt={selectedCombo.comboName || "Combo"}
                        className="h-full w-full object-contain"
                      />
                    </div>
                  )}

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-black text-gray-900">
                        {selectedCombo?.comboName || selectedCombo?.item_name}
                      </h2>
                      <span className="rounded-full bg-violet-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-violet-700">
                        {selectedCombo?.comboType || "Combo"}
                      </span>
                    </div>

                    <p className="mt-1 max-w-2xl text-xs leading-5 text-gray-500">
                      {selectedCombo?.comboDescription ||
                        selectedCombo?.item_description}
                    </p>

                    <div className="mt-2 text-lg font-black text-emerald-600">
                      ₹{Number(selectedCombo?.comboPrice || 0)}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={closeComboConfigurator}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100"
                >
                  Close
                </button>
              </div>

              <div className="flex-1 space-y-5 overflow-y-auto bg-gray-50 p-5">
                {(selectedCombo?.optionGroups || []).map((group) => {
                  const groupKey = String(group?.optionGroupId || "");
                  const selectedForGroup = comboSelections[groupKey] || [];
                  const minimum = Number(group?.minimumSelection || 0);
                  const maximum = Number(group?.maximumSelection || 0);

                  return (
                    <div
                      key={groupKey}
                      className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
                    >
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <h3 className="text-base font-black text-gray-900">
                            {group?.groupTitle || "Select item"}
                          </h3>
                          <p className="mt-0.5 text-[11px] text-gray-500">
                            Select {minimum}
                            {maximum !== minimum ? ` to ${maximum}` : ""}
                          </p>
                        </div>

                        <span
                          className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${
                            selectedForGroup.length >= minimum &&
                            selectedForGroup.length <= maximum
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {selectedForGroup.length}/{maximum || minimum}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {(group?.items || []).map((comboItem) => {
                          const prices =
                            Array.isArray(comboItem?.prices) &&
                            comboItem.prices.length > 0
                              ? comboItem.prices
                              : [
                                  {
                                    size_id: "",
                                    size: "",
                                    price: 0,
                                  },
                                ];

                          const selectedItem = selectedForGroup.find(
                            (choice) =>
                              String(choice?.itemId) ===
                              String(comboItem?.item_id || ""),
                          );

                          return (
                            <div
                              key={comboItem?.item_id}
                              className={`rounded-xl border p-3 transition ${
                                selectedItem
                                  ? "border-emerald-400 bg-emerald-50"
                                  : "border-gray-200 bg-white"
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                {comboItem?.photo && (
                                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-white p-1">
                                    <img
                                      src={comboItem.photo}
                                      alt={comboItem?.item_name || "Combo item"}
                                      className="h-full w-full object-contain"
                                      loading="lazy"
                                    />
                                  </div>
                                )}

                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-bold text-gray-900">
                                    {comboItem?.item_name}
                                  </div>
                                  {selectedItem && (
                                    <div className="mt-1 text-[10px] font-semibold text-emerald-700">
                                      Selected
                                      {selectedItem.variantName
                                        ? ` • ${selectedItem.variantName}`
                                        : ""}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="mt-3 flex flex-wrap gap-2">
                                {prices.map((price) => {
                                  const selected = isComboChoiceSelected(
                                    group,
                                    comboItem,
                                    price,
                                  );

                                  return (
                                    <button
                                      type="button"
                                      key={`${comboItem?.item_id}-${
                                        price?.size_id ||
                                        price?.size ||
                                        "default"
                                      }`}
                                      onClick={() =>
                                        selectComboChoice(
                                          group,
                                          comboItem,
                                          price,
                                        )
                                      }
                                      className={`rounded-lg border px-3 py-2 text-[11px] font-bold transition ${
                                        selected
                                          ? "border-emerald-500 bg-emerald-500 text-white"
                                          : "border-gray-300 bg-gray-50 text-gray-700 hover:border-violet-400 hover:bg-violet-50"
                                      }`}
                                    >
                                      {price?.size || "Select"}
                                    </button>
                                  );
                                })}

                                {maximum > 1 && selectedItem && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      removeComboChoice(
                                        group,
                                        comboItem?.item_id,
                                      )
                                    }
                                    className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-bold text-red-600 hover:bg-red-100"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-4">
                <div className="text-xs text-gray-500">
                  {isComboSelectionValid(selectedCombo)
                    ? "Combo selection complete"
                    : "Complete all required selections"}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={closeComboConfigurator}
                    className="rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={() => saveComboToCart(selectedCombo)}
                    disabled={!isComboSelectionValid(selectedCombo)}
                    className="rounded-xl bg-emerald-500 px-6 py-3 text-sm font-black text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {editingComboCartIndex !== null
                      ? "Update Combo"
                      : "Add Combo"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* customization model */}

        {showCustomize && hasSelectedCartItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="flex w-[800px] bg-white rounded-xl overflow-hidden shadow-xl">
              {/* LEFT SIDE */}
              <div className="flex-1 bg-yellow-50 p-6">
                <h2 className="text-xl font-bold text-yellow-600 text-center">
                  Customize
                </h2>

                <p className="text-center text-gray-600 mb-4">
                  {selectedCartItem?.item_name}
                </p>

                {/* Choose One */}
                <p className="font-semibold mb-2">Choose One:</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {["Spicy", "Non Spicy", "Gravy", "Dry", "No garlic"].map(
                    (opt) => (
                      <button
                        key={opt}
                        onClick={() =>
                          setSelectedOptions((s) => ({
                            ...s,
                            type: opt,
                          }))
                        }
                        className={`px-4 py-2 rounded border text-sm ${
                          selectedOptions.type === opt
                            ? "bg-yellow-400 text-white"
                            : "bg-white"
                        }`}
                      >
                        {opt}
                      </button>
                    ),
                  )}
                </div>

                {/* Add-ons */}
                <p className="font-semibold mb-2">Add-ons:</p>
                <div className="flex flex-wrap gap-2">
                  {["Onion", "Garlic", "Without Garlic"].map((opt) => {
                    const selected = selectedOptions.addons || [];

                    return (
                      <button
                        key={opt}
                        onClick={() => {
                          const updated = selected.includes(opt)
                            ? selected.filter((v) => v !== opt)
                            : [...selected, opt];

                          setSelectedOptions((s) => ({
                            ...s,
                            addons: updated,
                          }));
                        }}
                        className={`px-4 py-2 rounded border text-sm ${
                          selected.includes(opt)
                            ? "bg-blue-500 text-white"
                            : "bg-white"
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT SIDE */}
              <div className="w-64 bg-gray-100 flex flex-col justify-center items-center gap-4 p-6">
                <button
                  onClick={applyCustomization}
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 rounded-lg shadow"
                >
                  APPLY & CLOSE
                </button>

                <button
                  onClick={() => setShowCustomize(false)}
                  className="w-full bg-gray-300 hover:bg-gray-400 text-black font-bold py-4 rounded-lg shadow"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
